// src/components/notify/NotificationPreferences.js
// Notification settings: browser push on this device, do-not-disturb (mute / quiet hours),
// sound + pop-ups, per-category push/email, registered devices, and a test send.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, Button, Divider, Flex, FormControl, FormLabel, Heading, HStack,
    Icon, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Select, Skeleton, Stack, Switch, Table, Tbody, Td, Text, Th, Thead, Tooltip,
    Tr, useColorModeValue, useToast, VStack,
} from '@chakra-ui/react';
import {
    MdComputer, MdDeleteOutline, MdDoNotDisturbOn, MdLock, MdNotificationsActive, MdPhoneAndroid, MdPhoneIphone,
    MdSend, MdWeb,
} from 'react-icons/md';
import * as api from '../../services/notifyApi';
import * as webPush from '../../utils/notifyWebPush';
import { categoryMeta, fmtDateTime, timeAgo } from './notifyFormat';

const TIMEZONES = ['America/Nassau', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Europe/London', 'UTC'];
const MUTE_OPTIONS = [
    { label: '30 minutes', minutes: 30 },
    { label: '1 hour', minutes: 60 },
    { label: '4 hours', minutes: 240 },
    { label: '8 hours', minutes: 480 },
    { label: '24 hours', minutes: 1440 },
];
const PLATFORM_ICON = { web: MdWeb, android: MdPhoneAndroid, ios: MdPhoneIphone, desktop: MdComputer };

function Section({ title, children, right }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <Box>
            <Flex justify="space-between" align="center" mb={2}>
                <Heading size="xs" textTransform="uppercase" letterSpacing="wide" color={muted}>{title}</Heading>
                {right}
            </Flex>
            {children}
        </Box>
    );
}

export default function NotificationPreferences({ isOpen, onClose }) {
    const toast = useToast();
    const muted = useColorModeValue('gray.500', 'gray.400');
    const card = useColorModeValue('gray.50', 'whiteAlpha.50');
    const [doc, setDoc] = useState(null);
    const [devices, setDevices] = useState([]);
    const [push, setPush] = useState({ supported: false, enabled: false, permission: 'default' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);
    const [testing, setTesting] = useState(false);
    const [dirty, setDirty] = useState(false);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const load = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const [p, d, w] = await Promise.all([api.getPreferences(signal), api.getDevices(signal).catch(() => []), webPush.getState()]);
            if (!isMounted.current) return;
            setDoc({ ...p, settings: { ...p.settings, quiet_enabled: Boolean(p.settings.quiet_start && p.settings.quiet_end) } });
            setDevices(d || []);
            setPush(w);
            setDirty(false);
        } catch (e) {
            if (e?.name !== 'AbortError' && isMounted.current) setError(e);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        const ctrl = new AbortController();
        load(ctrl.signal);
        return () => ctrl.abort();
    }, [isOpen, load]);

    const setSetting = useCallback((k, v) => { setDoc(d => ({ ...d, settings: { ...d.settings, [k]: v } })); setDirty(true); }, []);
    const setCategory = useCallback((key, field, v) => {
        setDoc(d => ({ ...d, categories: d.categories.map(c => (c.key === key ? { ...c, [field]: v } : c)) }));
        setDirty(true);
    }, []);

    const save = useCallback(async (extra = {}) => {
        if (!doc) return;
        setSaving(true);
        try {
            const s = doc.settings;
            const body = {
                settings: {
                    quiet_start: s.quiet_enabled ? s.quiet_start || '22:00' : null,
                    quiet_end: s.quiet_enabled ? s.quiet_end || '06:00' : null,
                    timezone: s.timezone,
                    critical_override: s.critical_override,
                    sound: s.sound,
                    desktop: s.desktop,
                    ...(extra.mute_minutes ? { mute_minutes: extra.mute_minutes } : extra.unmute ? {} : s.muted_now && s.mute_until ? { mute_until: s.mute_until } : {}),
                },
                categories: doc.categories.map(c => ({ key: c.key, push: c.push, email: c.email })),
            };
            const next = await api.savePreferences(body);
            if (!isMounted.current) return;
            setDoc({ ...next, settings: { ...next.settings, quiet_enabled: Boolean(next.settings.quiet_start && next.settings.quiet_end) } });
            setDirty(false);
            toast({ status: 'success', title: extra.mute_minutes ? 'Notifications muted' : extra.unmute ? 'Notifications back on' : 'Notification settings saved', duration: 2500 });
        } catch (e) {
            toast({ status: 'error', title: 'Could not save settings', description: e.message });
        } finally {
            if (isMounted.current) setSaving(false);
        }
    }, [doc, toast]);

    const togglePush = useCallback(async () => {
        setPushBusy(true);
        try {
            if (push.enabled) await webPush.disable();
            else await webPush.enable();
            const [w, d] = await Promise.all([webPush.getState(), api.getDevices().catch(() => devices)]);
            if (!isMounted.current) return;
            setPush(w);
            setDevices(d);
            toast({ status: 'success', title: w.enabled ? 'Browser push is on for this device' : 'Browser push turned off', duration: 2500 });
        } catch (e) {
            toast({ status: 'warning', title: 'Browser push', description: e.message });
        } finally {
            if (isMounted.current) setPushBusy(false);
        }
    }, [push.enabled, devices, toast]);

    const removeDevice = useCallback(async (d) => {
        try {
            await api.removeDevice(d.id);
            if (isMounted.current) setDevices(list => list.filter(x => x.id !== d.id));
            if (d.platform === 'web' && push.enabled) setPush(await webPush.getState());
        } catch (e) { toast({ status: 'error', title: 'Could not remove device', description: e.message }); }
    }, [push.enabled, toast]);

    const test = useCallback(async () => {
        setTesting(true);
        try {
            await api.sendTest();
            toast({ status: 'info', title: 'Test sent', description: 'It should appear in your notification centre within a second.', duration: 3000 });
        } catch (e) {
            toast({ status: 'error', title: 'Test failed', description: e.message });
        } finally {
            if (isMounted.current) setTesting(false);
        }
    }, [toast]);

    const s = doc?.settings;
    const mutedLabel = useMemo(() => (s?.muted_now && s?.mute_until ? `Muted until ${fmtDateTime(s.mute_until)}` : null), [s]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <HStack><Icon as={MdNotificationsActive} color="purple.500" /><Text>Notification settings</Text></HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {error ? (
                        <Alert status="error" borderRadius="lg">
                            <AlertIcon /><AlertDescription flex="1">{error.message}</AlertDescription>
                            <Button size="sm" onClick={() => load()}>Retry</Button>
                        </Alert>
                    ) : loading || !doc ? (
                        <Stack spacing={3}>{[0, 1, 2, 3].map(i => <Skeleton key={i} h="56px" borderRadius="lg" />)}</Stack>
                    ) : (
                        <VStack align="stretch" spacing={6}>
                            <Section title="This device">
                                <Box bg={card} borderRadius="lg" p={3}>
                                    <Flex justify="space-between" align="center" gap={3}>
                                        <Box>
                                            <Text fontWeight="700" fontSize="sm">Browser push</Text>
                                            <Text fontSize="xs" color={muted}>
                                                {!push.supported ? 'Not supported by this browser.'
                                                    : push.permission === 'denied' ? 'Blocked in this browser’s site settings — allow notifications for FlowSense to turn this on.'
                                                        : 'Get notifications even when FlowSense is closed. Signed by FlowSense (VAPID) — no Firebase.'}
                                            </Text>
                                        </Box>
                                        <Switch colorScheme="purple" isChecked={push.enabled} isDisabled={!push.supported || push.permission === 'denied' || pushBusy} onChange={togglePush} aria-label="Browser push on this device" />
                                    </Flex>
                                    <Button mt={3} size="sm" variant="outline" leftIcon={<MdSend />} onClick={test} isLoading={testing}>Send me a test notification</Button>
                                </Box>
                            </Section>

                            <Section title="Do not disturb" right={mutedLabel && <Badge colorScheme="orange">{mutedLabel}</Badge>}>
                                <Stack spacing={3}>
                                    <HStack>
                                        <Menu>
                                            <MenuButton as={Button} size="sm" leftIcon={<MdDoNotDisturbOn />} variant="outline" isLoading={saving}>Mute for…</MenuButton>
                                            <MenuList>
                                                {MUTE_OPTIONS.map(o => <MenuItem key={o.minutes} onClick={() => save({ mute_minutes: o.minutes })}>{o.label}</MenuItem>)}
                                            </MenuList>
                                        </Menu>
                                        {s.muted_now && <Button size="sm" variant="ghost" onClick={() => save({ unmute: true })}>Unmute</Button>}
                                    </HStack>
                                    <Flex align="center" justify="space-between" gap={3}>
                                        <Box>
                                            <Text fontSize="sm" fontWeight="600">Quiet hours</Text>
                                            <Text fontSize="xs" color={muted}>Notifications still arrive in your inbox — just without pop-ups, sound or browser push.</Text>
                                        </Box>
                                        <Switch colorScheme="purple" isChecked={s.quiet_enabled} onChange={(e) => setSetting('quiet_enabled', e.target.checked)} aria-label="Quiet hours" />
                                    </Flex>
                                    {s.quiet_enabled && (
                                        <HStack spacing={3} flexWrap="wrap">
                                            <FormControl w="auto"><FormLabel fontSize="xs" mb={1}>From</FormLabel><Input size="sm" type="time" value={s.quiet_start || '22:00'} onChange={(e) => setSetting('quiet_start', e.target.value)} /></FormControl>
                                            <FormControl w="auto"><FormLabel fontSize="xs" mb={1}>To</FormLabel><Input size="sm" type="time" value={s.quiet_end || '06:00'} onChange={(e) => setSetting('quiet_end', e.target.value)} /></FormControl>
                                            <FormControl w="auto" minW="200px"><FormLabel fontSize="xs" mb={1}>Time zone</FormLabel>
                                                <Select size="sm" value={s.timezone} onChange={(e) => setSetting('timezone', e.target.value)}>
                                                    {[...new Set([s.timezone, ...TIMEZONES])].map(tz => <option key={tz} value={tz}>{tz}</option>)}
                                                </Select>
                                            </FormControl>
                                        </HStack>
                                    )}
                                    <Flex align="center" justify="space-between" gap={3}>
                                        <Box>
                                            <Text fontSize="sm" fontWeight="600">Critical alerts break through</Text>
                                            <Text fontSize="xs" color={muted}>Critical notifications ignore mute and quiet hours (recommended for on-call staff).</Text>
                                        </Box>
                                        <Switch colorScheme="red" isChecked={s.critical_override} onChange={(e) => setSetting('critical_override', e.target.checked)} aria-label="Critical override" />
                                    </Flex>
                                </Stack>
                            </Section>

                            <Section title="Sound & pop-ups">
                                <Stack spacing={2}>
                                    <Flex justify="space-between" align="center"><Text fontSize="sm">Play a sound for high and critical notifications</Text><Switch colorScheme="purple" isChecked={s.sound} onChange={(e) => setSetting('sound', e.target.checked)} aria-label="Sound" /></Flex>
                                    <Flex justify="space-between" align="center"><Text fontSize="sm">Desktop pop-ups when FlowSense is in the background</Text><Switch colorScheme="purple" isChecked={s.desktop} onChange={(e) => setSetting('desktop', e.target.checked)} aria-label="Desktop pop-ups" /></Flex>
                                </Stack>
                            </Section>

                            <Section title="By topic">
                                <Box overflowX="auto">
                                    <Table size="sm">
                                        <Thead><Tr><Th>Topic</Th><Th textAlign="center">Push / pop-up</Th><Th textAlign="center">Email</Th></Tr></Thead>
                                        <Tbody>
                                            {doc.categories.map(c => {
                                                const meta = categoryMeta(c.key);
                                                return (
                                                    <Tr key={c.key}>
                                                        <Td>
                                                            <HStack spacing={2}><Icon as={meta.icon} color={`${meta.color}.500`} /><Box><Text fontSize="sm" fontWeight="600">{c.label}</Text><Text fontSize="xs" color={muted}>{c.description}</Text></Box></HStack>
                                                        </Td>
                                                        <Td textAlign="center">
                                                            {c.mutable ? (
                                                                <Switch size="sm" colorScheme="purple" isChecked={c.push} onChange={(e) => setCategory(c.key, 'push', e.target.checked)} aria-label={`Push for ${c.label}`} />
                                                            ) : (
                                                                <Tooltip label="Always on — operational safety traffic" hasArrow><span><Icon as={MdLock} color={muted} aria-label="Always on" /></span></Tooltip>
                                                            )}
                                                        </Td>
                                                        <Td textAlign="center"><Switch size="sm" colorScheme="purple" isChecked={c.email} onChange={(e) => setCategory(c.key, 'email', e.target.checked)} aria-label={`Email for ${c.label}`} /></Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </Box>
                            </Section>

                            <Section title={`Devices (${devices.length})`}>
                                {devices.length === 0 ? (
                                    <Text fontSize="sm" color={muted}>No devices yet. Turn on browser push above, or sign in to the FlowSense tech app.</Text>
                                ) : (
                                    <VStack align="stretch" spacing={2}>
                                        {devices.map(d => (
                                            <Flex key={d.id} bg={card} borderRadius="lg" px={3} py={2} align="center" gap={3}>
                                                <Icon as={PLATFORM_ICON[d.platform] || MdComputer} boxSize={5} color={muted} />
                                                <Box flex="1" minW={0}>
                                                    <Text fontSize="sm" fontWeight="600" noOfLines={1}>{d.device_name || (d.transport === 'webpush' ? 'Browser' : 'FlowSense tech app')}{d.app_version ? ` · v${d.app_version}` : ''}</Text>
                                                    <Text fontSize="xs" color={muted}>{d.transport === 'webpush' ? 'Browser push' : d.transport === 'fcm' ? 'Mobile push' : 'Live stream'} · last seen {timeAgo(d.last_seen_at)}{d.last_error ? ` · ${d.last_error}` : ''}</Text>
                                                </Box>
                                                {!d.enabled && <Badge colorScheme="red">Disabled</Badge>}
                                                <Tooltip label="Remove device" hasArrow><IconButton size="sm" variant="ghost" icon={<MdDeleteOutline />} aria-label="Remove device" onClick={() => removeDevice(d)} /></Tooltip>
                                            </Flex>
                                        ))}
                                    </VStack>
                                )}
                            </Section>
                        </VStack>
                    )}
                </ModalBody>
                <Divider />
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                    <Button colorScheme="purple" onClick={() => save()} isLoading={saving} isDisabled={!dirty || loading}>Save settings</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
